'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Mail, Lock, Loader2, User } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { signIn, getSession } from 'next-auth/react';
import Swal from 'sweetalert2';
import GoogleLogo from '@/assets/images/google.png';
import Logo from '@/assets/images/fusha.png';
import Image from 'next/image';
import { Capacitor } from '@capacitor/core';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';

export default function RegisterPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_SIGN_IN_CLIENT_ID || "";

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      GoogleSignIn.initialize({
        clientId: googleClientId,
      }).catch(console.error);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Nama wajib diisi'),
      email: Yup.string().email('Email tidak valid').required('Email wajib diisi'),
      password: Yup.string().min(8, 'Minimal 8 karakter').required('Password wajib diisi'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Password tidak sama')
        .required('Konfirmasi password wajib diisi'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const trimmed = values.name.trim();
      const parts = trimmed.split(/\s+/).filter(Boolean);
      const firstName = parts[0] ?? '';
      const lastName = parts.length > 1 ? parts.slice(1).join(' ') : firstName;

      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/auth/register`, {
          method: 'POST',
          body: JSON.stringify({
            user: {
              firstName,
              lastName,
              email: values.email,
              password: values.password,
              originUrl: 'annajm_learn',
            },
          }),
          headers: { 'Content-Type': 'application/json' },
        });
        const result = await res.text();

        if (
          result.indexOf('Please input better password') === -1 &&
          result.indexOf('email already used') === -1
        ) {
          setIsLoading(false);
          Swal.fire({
            title: 'Cek email kamu',
            text: 'Link verifikasi telah dikirim ke email kamu. Buka email dan klik tombol verifikasi untuk mengaktifkan akun.',
            icon: 'success',
            confirmButtonText: 'Ke halaman masuk',
            confirmButtonColor: '#804EEC',
          }).then(() => {
            router.push('/login');
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: result,
          });
          setIsLoading(false);
        }
      } catch {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Gagal mendaftar. Coba lagi atau periksa koneksi.',
        });
        setIsLoading(false);
      }
    },
  });

  const inputClass =
    'w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-brand-purpleText focus:ring-2 focus:ring-brand-purpleText/25';

  return (
    <div className="relative min-h-screen overflow-hidden bg-brand-background">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -left-20 top-0 h-[420px] w-[420px] rounded-full bg-purple-400/25 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-[380px] w-[380px] rounded-full bg-fuchsia-400/20 blur-3xl" />
        <div className="absolute left-1/2 top-24 h-px w-[min(100%,720px)] -translate-x-1/2 bg-gradient-to-r from-transparent via-purple-200/60 to-transparent" />
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-10 sm:py-14">
        <div className="w-full max-w-[440px]">
          <div className="overflow-hidden rounded-2xl border border-brand-border bg-white shadow-[0_24px_64px_-12px_rgba(88,28,135,0.15)]">
            <div className="relative bg-gradient-to-b from-violet-200 via-violet-50 to-white pt-12 pb-8">
              <div className="flex justify-center">
                <Link href="/" aria-label="Beranda Fusha" className="inline-flex">
                  <Image src={Logo} alt="Logo" width={220} height={36} priority />
                </Link>
              </div>
            </div>

            <div className="px-8 pb-10 pt-8 sm:px-10">
              <h1 className="text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-[1.65rem]">
                Buat akun
              </h1>
              <p className="mt-2 text-center text-sm text-gray-600">
                Mulai belajar Bahasa Arab bersama Fusha
              </p>

              <div className="mt-8">
                <button
                  type="button"
                  disabled={isLoading}
                  aria-busy={isLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-800 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      if (Capacitor.isNativePlatform()) {
                        const result = await GoogleSignIn.signIn();
                        console.log('result google', result);
                        const res = await signIn('google-capacitor', {
                          redirect: false,
                          profile: JSON.stringify(result),
                        });
                        console.log('nextauth res', res);

                        if (!res?.ok || res.error) {
                          console.error('nextauth error', res?.error);
                          Swal.fire({
                            icon: 'error',
                            title: 'Oops...',
                            text: 'Pendaftaran Google gagal',
                          });
                          return;
                        }

                        await getSession();
                        console.log('redirecting app');
                        window.location.assign('/app');
                      } else {
                        await signIn('google', { callbackUrl: `${window.location.origin}/app` });
                      }
                    } catch (error) {
                      console.error('Google register error', error);
                      Swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: 'Pendaftaran gagal. Silakan coba lagi.',
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-brand-purpleText" />
                  ) : (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={GoogleLogo.src} alt="" className="h-[18px] w-[18px]" width={18} height={18} />
                      <span>Lanjutkan dengan Google</span>
                    </>
                  )}
                </button>
              </div>

              <div className="my-8 flex items-center gap-3">
                <hr className="min-h-px flex-1 border-0 bg-gradient-to-r from-transparent via-gray-200 to-gray-200" />
                <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-gray-400">
                  atau email
                </span>
                <hr className="min-h-px flex-1 border-0 bg-gradient-to-l from-transparent via-gray-200 to-gray-200" />
              </div>

              <form className="space-y-5" onSubmit={formik.handleSubmit} noValidate>
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-gray-800">
                    Nama lengkap
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      placeholder="Nama kamu"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClass}
                    />
                  </div>
                  {formik.touched.name && formik.errors.name && (
                    <p className="mt-1.5 text-sm text-red-600">{formik.errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-800">
                    Email
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="nama@email.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClass}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <p className="mt-1.5 text-sm text-red-600">{formik.errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-gray-800">
                    Password
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Minimal 8 karakter"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClass}
                    />
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="mt-1.5 text-sm text-red-600">{formik.errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium text-gray-800">
                    Konfirmasi password
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Ulangi password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputClass}
                    />
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <p className="mt-1.5 text-sm text-red-600">{formik.errors.confirmPassword}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  aria-busy={isLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-purpleText py-3.5 text-sm font-semibold text-white shadow-md shadow-purple-900/10 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Daftar'}
                </button>
              </form>

              <p className="mt-8 text-center text-sm text-gray-500">
                Sudah punya akun?{' '}
                <Link
                  href="/login"
                  className="font-semibold text-brand-purpleText underline-offset-2 hover:underline"
                >
                  Masuk
                </Link>
              </p>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-gray-500">
            Dengan mendaftar, kamu menyetujui penggunaan akun sesuai kebijakan layanan Fusha.
          </p>
        </div>
      </div>
    </div>
  );
}
